// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";
import { hideAll, Instance as TippyInstance, sticky } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import { RugPopupInterface } from "@rugby/App/Components/Tippy/Interface";
import { rugPopupStyles } from "@rugby/App/Components/Tippy/Style";
import { StyledOverlay } from "@rugby/App/Components/XngMenu";
import { xCommonUpdate } from "@rugby/App/Handlers/XngCommoen";
import { XCommonState, xHeaderState } from "@rugby/App/Store/Reducers";
import { constants } from "@rugby/XDL/constants";
import { EventBus, isMobileDevice } from "@rugby/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: xHeaderState & XCommonState) => ({
    isSticky: state.xHeader.isSticky,
    tippyVisibility: state.xCommon.tippyVisibility,
});

const mapDispatchToProps = {
    xCommonUpdate,
};

type Props = RugPopupInterface &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    ThemeProps<BaseTheme> &
    TippyProps;

const defaultProps: TippyProps = {
    placement: "bottom",
    delay: 250,
    maxWidth: "none",
    interactive: true,
    trigger: "mouseenter focus",
    offset: [0, 14],
    animation: "shift-away",
    sticky: "reference",
    plugins: [sticky],
    arrow: true,
    inertia: false,
};

const TippyPopup: FC<Props> = ({
    onPopupVisibilityUpdate: onPopupVisibilityUpdateProps,
    enableMobileMode,
    updateTriggerOptionsOnMobile,
    extendedStyles,
    isSticky,
    children,
    xCommonUpdate,
    tippyVisibility,
    ...otherProps
}) => {
    const {
        onCreate: onCreateProps,
        content: tippyContent,
        onHide: onHideProps,
        onShow: onShowProps,
        ...tippyProps
    } = otherProps;

    const [styles] = useState(() => mergeToNew(rugPopupStyles, extendedStyles));

    const [customOptions, setCustomOptions] = useState({});
    const [tippyInstance, setTippyInstance] = useState<TippyInstance>();
    const [isPopupVisible, setPopupVisibility] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // we need it to inject state to window.addEventListener
    const stateRef = React.useRef({
        isMobile,
        isSticky,
    });

    const applyCssFiles = () => {
        /* eslint-disable global-require */
        require("tippy.js/dist/tippy.css");
        require("tippy.js/animations/shift-away.css");
        /* eslint-enable global-require */
    };

    useEffect(() => {
        applyCssFiles();
        window.addEventListener("resize", () => setIsMobile(isMobileDevice()), true);
        setIsMobile(isMobileDevice());
    }, []);

    useEffect(() => {
        if (stateRef.current.isMobile !== isMobile) {
            // Rugby Customization
            const customOptions = customOptionsOnMobileMode(isMobile);
            setCustomOptions(customOptions);
        }

        if (tippyVisibility === "visible" && isMobile) {
            xCommonUpdate({ data: { type: "tippyVisibility", value: "hidden" } });
        } else if (!isMobile && isPopupVisible && tippyVisibility !== "visible") {
            xCommonUpdate({ data: { type: "tippyVisibility", value: "visible" } });
        }

        stateRef.current = { isSticky, isMobile };
    }, [isSticky, isMobile]);

    const closeButtonHandler = () => {
        tippyInstance && tippyInstance.hide();
    };

    const contentWrapper = (
        <>
            <Icon {...styles.closeButton} onClick={closeButtonHandler} />
            <StyledWrapper className="content-wrapper">{tippyContent}</StyledWrapper>
        </>
    );

    const onHide = (instance: TippyInstance) => {
        setPopupVisibility(false);
        xCommonUpdate({ data: { type: "tippyVisibility", value: "hidden" } });
        onPopupVisibilityUpdateProps && onPopupVisibilityUpdateProps(false);
        onHideProps && onHideProps(instance);
    };

    const onShow = (instance: TippyInstance) => {
        // hide another TippyPopup
        hideAll();

        // close Main Navigation
        EventBus.dispatch(constants.eventBus.mainNavigation.key, {
            type: constants.eventBus.mainNavigation.types.hideMenu,
        });

        !isMobile && xCommonUpdate({ data: { type: "tippyVisibility", value: "visible" } });
        setPopupVisibility(true);
        onPopupVisibilityUpdateProps && onPopupVisibilityUpdateProps(true);
        onShowProps && onShowProps(instance);
    };

    // Rugby customization
    const customOptionsOnMobileMode = (isMobileDevice: boolean) => {
        // reset options
        const props = { ...defaultProps, ...otherProps };
        let options = {
            arrow: props.arrow,
            animation: props.animation,
            inertia: props.inertia,
            placement: props.placement,
            trigger: props.trigger,
        };

        if (isMobileDevice) {
            options = {
                ...options,
                arrow: false,
            };

            if (updateTriggerOptionsOnMobile) {
                options = {
                    ...options,
                    trigger: "click",
                };
            }

            if (enableMobileMode) {
                options = {
                    ...options,
                    animation: "shift-away",
                    inertia: true,
                    placement: "left",
                };
            }
        }

        return options;
    };

    return (
        <StyledWrapper {...styles.main} {...{ enableMobileMode }}>
            <StyledOverlay isVisible={isMobile && isPopupVisible} className="tippyVisibility_overlay" />
            <Tippy
                onCreate={instance => {
                    setTippyInstance(instance);
                    onCreateProps && onCreateProps(instance);
                }}
                onHide={onHide}
                onShow={onShow}
                content={contentWrapper}
                {...tippyProps}
                {...customOptions}
            >
                <span style={styles.elementWrapper}>{children}</span>
            </Tippy>
        </StyledWrapper>
    );
};

TippyPopup.defaultProps = {
    ...(defaultProps as any),
    enableMobileMode: true,
    updateTriggerOptionsOnMobile: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(TippyPopup));
